import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import Cookies from 'js-cookie'

import { User, AuthResponse } from '../types'
import { authApi } from '../services/api'

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  
  // Actions
  login: (token: string, user: User) => void
  logout: () => void
  setUser: (user: User) => void
  updateUser: (updates: Partial<User>) => void
  requestCode: (email: string) => Promise<void>
  verifyCodeOnly: (email: string, code: string) => Promise<{ user_exists: boolean; email: string }>
  completeAuth: (email: string, userData?: { username?: string; city?: string; address?: string }) => Promise<AuthResponse>
  verifyCode: (email: string, code: string, userData?: { username?: string; city?: string; address?: string }) => Promise<AuthResponse>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,

      login: (token: string, user: User) => {
        // Store token in cookie for automatic inclusion in API requests
        Cookies.set('auth_token', token, { 
          expires: 1, // 1 day
          secure: window.location.protocol === 'https:',
          sameSite: 'strict'
        })
        
        set({ 
          user, 
          token, 
          isAuthenticated: true,
          isLoading: false 
        })
      },

      logout: () => {
        Cookies.remove('auth_token')
        set({ 
          user: null, 
          token: null, 
          isAuthenticated: false,
          isLoading: false 
        })
      },

      setUser: (user: User) => {
        set({ user })
      },

      updateUser: (updates: Partial<User>) => {
        const currentUser = get().user
        if (currentUser) {
          set({ user: { ...currentUser, ...updates } })
        }
      },

      requestCode: async (email: string) => {
        set({ isLoading: true })
        try {
          await authApi.requestCode({ email })
        } finally {
          set({ isLoading: false })
        }
      },

      verifyCodeOnly: async (email: string, code: string) => {
        set({ isLoading: true })
        try {
          const response = await authApi.verifyCodeOnly({ email, code })
          return response
        } finally {
          set({ isLoading: false })
        }
      },

      completeAuth: async (email: string, userData?: { username?: string; city?: string; address?: string }) => {
        set({ isLoading: true })
        try {
          const response = await authApi.completeAuth({
            email,
            ...userData
          })
          
          get().login(response.token, response.user)
          return response
        } finally {
          set({ isLoading: false })
        }
      },

      verifyCode: async (email: string, code: string, userData?: { username?: string; city?: string; address?: string }) => {
        set({ isLoading: true })
        try {
          const response = await authApi.verifyCode({
            email,
            code,
            ...userData
          })
          
          get().login(response.token, response.user)
          return response
        } finally {
          set({ isLoading: false })
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        // Check if token exists in cookie and sync with store
        const cookieToken = Cookies.get('auth_token')
        if (cookieToken && state?.token !== cookieToken) {
          // Token exists in cookie but not in store - sync
          if (state) {
            state.token = cookieToken
            state.isAuthenticated = !!state.user
          }
        } else if (!cookieToken && state?.token) {
          // Token in store but not in cookie - clear store
          if (state) {
            state.logout()
          }
        }
      }
    }
  )
)